import axios from 'axios'
import { Message } from 'element-ui'
import store from '../store'
import { isProjectScopedAdmin } from './permission'
import { guardRequestAuth, handleAuthHttpError, extractResponseMessage } from './authSession'

const request = axios.create({
  baseURL: '/api',
  timeout: 30000
})

request.interceptors.request.use(config => {
  const blocked = guardRequestAuth(config)
  if (blocked) return blocked
  if (!config.skipAuth) {
    const token = store.state.token
    if (token) {
      config.headers.Authorization = 'Bearer ' + token
    }
    const user = store.state.user
    if (user && isProjectScopedAdmin(user) && store.state.selectedProjectGroupId) {
      config.headers['X-Project-Group-Id'] = String(store.state.selectedProjectGroupId)
    }
  }
  return config
})

request.interceptors.response.use(
  res => {
    const data = res.data
    if (data && data.code === 401) {
      return handleAuthHttpError(401, data, res.config)
    }
    if (!data || data.code !== 200) {
      if (!res.config.silent) {
        Message.error((data && data.message) || '请求失败')
      }
      const err = new Error((data && data.message) || '请求失败')
      if (data && typeof data === 'object') {
        err.code = data.code
        err.data = data
      }
      return Promise.reject(err)
    }
    return data
  },
  err => {
    const cfg = err.config || {}
    if (err.response && (err.response.status === 401 || err.response.status === 403)) {
      const handled = handleAuthHttpError(err.response.status, err.response.data, cfg)
      if (handled) return handled
    }
    if (err.response) {
      const payload = err.response.data
      const msg = extractResponseMessage(payload)
      const status = err.response.status
      if (!cfg.silent) {
        if (msg) {
          Message.error(msg)
        } else if (status === 409) {
          Message.error('数据已存在，请勿重复提交')
        } else if (status === 502 || status === 503 || status === 504) {
          Message.error('后端未启动或正在重启，请双击「启动项目.bat」后刷新页面')
        } else if (status >= 500) {
          Message.error('服务器错误，请查看 backend/run.log')
        } else {
          Message.error('请求失败，请稍后重试')
        }
      }
    } else if (
      err.code === 'ECONNREFUSED'
      || (err.message && (
        err.message.includes('Network Error')
        || err.message.includes('Proxy error')
        || err.message.includes('ECONNREFUSED')
      ))
    ) {
      if (!cfg.silent) {
        Message.error('无法连接后端(8080)，请双击「启动项目.bat」启动后再试')
      }
      return Promise.reject(new Error('无法连接后端(8080)，请双击「启动项目.bat」启动后再试'))
    } else if (err.code === 'ECONNABORTED' || (err.message && err.message.includes('timeout'))) {
      if (!cfg.silent) {
        Message.error('请求超时，服务器可能繁忙，请稍后重试')
      }
    } else if (!cfg.silent) {
      Message.error('网络异常')
    }
    return Promise.reject(normalizeError(err.response && err.response.data ? err.response.data : err))
  }
)

function normalizeError(err) {
  if (err instanceof Error && err.message && err.message !== '[object Object]') {
    if (!err.data && err.response && err.response.data) {
      err.data = err.response.data
    }
    return err
  }
  if (typeof err === 'string') {
    const msg = extractResponseMessage(err) || err
    const e = new Error(msg)
    e.data = { message: msg }
    return e
  }
  if (err && typeof err === 'object') {
    const msg = extractResponseMessage(err) || err.message || err.msg || err.error || '请求失败'
    const e = new Error(typeof msg === 'string' ? msg : '请求失败')
    if (err.code != null) e.code = err.code
    e.data = err
    return e
  }
  return new Error(String(err || '请求失败'))
}

export default request
